var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { invariant } from "outvariant";
import { getCallFrame } from '../utils/internal/getCallFrame.mjs';
import { isIterable } from '../utils/internal/isIterable.mjs';
class RequestHandler {
  constructor(args) {
    this.resolver = args.resolver;
    this.options = args.options;
    const callFrame = getCallFrame(new Error());
    this.info = __spreadProps(__spreadValues({}, args.info), {
      callFrame
    });
    this.isUsed = false;
  }
  /**
   * Parse the intercepted request to extract additional information from it.
   * Parsed result is then exposed to other methods of this request handler.
   */
  parse(_args) {
    return __async(this, null, function* () {
      return {};
    });
  }
  /**
   * Test if this handler matches the given request.
   */
  test(args) {
    return __async(this, null, function* () {
      const parsedResult = yield this.parse({
        request: args.request,
        resolutionContext: args.resolutionContext
      });
      return this.predicate({
        request: args.request,
        parsedResult,
        resolutionContext: args.resolutionContext
      });
    });
  }
  extendResolverArgs(_args) {
    return {};
  }
  /**
   * Execute this request handler and produce a mocked response
   * using the given resolver function.
   */
  run(args) {
    return __async(this, null, function* () {
      var _a, _b;
      if (this.isUsed && ((_a = this.options) == null ? void 0 : _a.once)) {
        return null;
      }
      const mainRequestRef = args.request.clone();
      const parsedResult = yield this.parse({
        request: args.request,
        resolutionContext: args.resolutionContext
      });
      const shouldInterceptRequest = this.predicate({
        request: args.request,
        parsedResult,
        resolutionContext: args.resolutionContext
      });
      if (!shouldInterceptRequest) {
        return null;
      }
      if (this.isUsed && ((_b = this.options) == null ? void 0 : _b.once)) {
        return null;
      }
      this.isUsed = true;
      const executeResolver = this.wrapResolver(this.resolver);
      const resolverExtras = this.extendResolverArgs({
        request: args.request,
        parsedResult
      });
      const mockedResponse = yield executeResolver(__spreadProps(__spreadValues({}, resolverExtras), {
        request: args.request
      }));
      const executionResult = this.createExecutionResult({
        // Pass the cloned request to the result so that logging
        // and other consumers could read its body once more.
        request: mainRequestRef,
        response: mockedResponse,
        parsedResult
      });
      return executionResult;
    });
  }
  wrapResolver(resolver) {
    return (info) => __async(this, null, function* () {
      const result = this.resolverGenerator || (yield resolver(info));
      if (isIterable(result)) {
        this.isUsed = false;
        const { value, done } = result[Symbol.iterator]().next();
        const nextResponse = yield value;
        if (done) {
          this.isUsed = true;
        }
        if (!nextResponse && done) {
          invariant(
            this.resolverGeneratorResult,
            "Failed to returned a previously stored generator response: the value is not a valid Response."
          );
          return this.resolverGeneratorResult.clone();
        }
        if (!this.resolverGenerator) {
          this.resolverGenerator = result;
        }
        if (nextResponse) {
          this.resolverGeneratorResult = nextResponse == null ? void 0 : nextResponse.clone();
        }
        return nextResponse;
      }
      return result;
    });
  }
  createExecutionResult(args) {
    return {
      handler: this,
      request: args.request,
      response: args.response,
      parsedResult: args.parsedResult
    };
  }
}
export {
  RequestHandler
};
